import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle, BookOpen, Bot, Clapperboard, Database, FileText,
  Film, Loader2, MessageSquareText, Plus, RefreshCw, Save, Send, Sparkles,
  ToggleLeft, ToggleRight
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { authenticatedFetch } from "../../utils/api";
import { API_HOST } from "../../utils/constants";
import { useTheme } from "../../context/ThemeContext";
import { useToast } from "../../context/ToastContext";
import { isMockAgentEnabled, setMockAgentEnabled } from "./mockAgentProvider";
import { runTaskToCompletion } from "../../services/taskService";

const SURFACE = {
  page: "var(--color-bg-primary)",
  panel: "var(--color-bg-secondary)",
  card: "var(--color-bg-card)",
  soft: "var(--color-bg-tertiary)",
  input: "var(--color-bg-overlay)",
};

const TONES = {
  ready: { borderColor: "var(--color-success)", backgroundColor: "var(--color-green-light)", color: "var(--color-success)" },
  active: { borderColor: "var(--color-accent-primary)", backgroundColor: "var(--color-accent-light)", color: "var(--color-accent-primary)" },
  muted: { borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card, color: "var(--color-text-muted)" },
  warning: { borderColor: "var(--color-warning)", backgroundColor: "rgba(212,167,68,0.12)", color: "var(--color-warning)" },
};

const episodeLabel = (episode, index = 0) => {
  const order = episode?.sortOrder || index + 1;
  return `第${order}集`;
};

const shortText = (value, max = 90) => {
  const text = value || "";
  return text.length > max ? `${text.slice(0, max)}...` : text;
};

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
};

const compactValueText = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) return value.map(compactValueText).filter(Boolean).join("；");
  if (typeof value === "object") {
    const direct = firstText(value.description, value.summary, value.content, value.text, value.title, value.name);
    if (direct) return direct;
    return Object.entries(value)
      .map(([key, item]) => {
        const text = compactValueText(item);
        return text ? `${key}：${text}` : "";
      })
      .filter(Boolean)
      .join("\n");
  }
  return String(value);
};

const formatOptionList = (title, options) => {
  if (!Array.isArray(options) || !options.length) return "";
  const lines = options.map((option, index) => {
    const label = firstText(option?.title, option?.name, `选项${index + 1}`);
    const text = compactValueText(option);
    const mark = option?.recommended ? "（推荐）" : "";
    return `${index + 1}. ${label}${mark}${text && text !== label ? `：${text}` : ""}`;
  });
  return `${title}\n${lines.join("\n")}`;
};

const formatDirectorNotes = (data, fallbackNotes) => {
  const parts = [];
  const sourceCoverage = compactValueText(data?.sourceCoverage);
  if (sourceCoverage) parts.push(`本集原文覆盖\n${sourceCoverage}`);
  const opening = formatOptionList("开头写法选项", data?.openingOptions);
  if (opening) parts.push(opening);
  const ending = formatOptionList("结尾钩子选项", data?.cliffhangerOptions);
  if (ending) parts.push(ending);
  const rewriteBoundary = compactValueText(data?.rewriteBoundary);
  if (rewriteBoundary) parts.push(`改写边界\n${rewriteBoundary}`);
  if (fallbackNotes) parts.push(fallbackNotes);
  return parts.filter(Boolean).join("\n\n");
};

const episodeDialogueMemoryKey = (projectId, episodeId) => `adaptation_episode_dialogue_${projectId || "global"}_${episodeId || "none"}`;
const episodeDraftMemoryKey = (projectId, episodeNumber) => `adaptation_episode_draft_${projectId || "global"}_${episodeNumber || "none"}`;
const PRODUCTION_WORKSPACE_STATE_KEY = "episode_production_workbench";

const normalizeProductionMemory = (value) => ({
  dialogues: value?.dialogues && typeof value.dialogues === "object" ? value.dialogues : {},
  drafts: value?.drafts && typeof value.drafts === "object" ? value.drafts : {},
});

const sameJson = (left, right) => {
  try {
    return JSON.stringify(left || null) === JSON.stringify(right || null);
  } catch {
    return false;
  }
};

const readEpisodeDialogue = (projectId, episodeId) => {
  if (!episodeId) return [];
  try {
    const raw = window.localStorage.getItem(episodeDialogueMemoryKey(projectId, episodeId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeEpisodeDialogue = (projectId, episodeId, messages) => {
  if (!episodeId) return;
  try {
    window.localStorage.setItem(episodeDialogueMemoryKey(projectId, episodeId), JSON.stringify((messages || []).slice(-30)));
  } catch {
    // 本地对话记忆失败不影响剧本生成。
  }
};

const removeEpisodeDialogue = (projectId, episodeId) => {
  if (!episodeId) return;
  try {
    window.localStorage.removeItem(episodeDialogueMemoryKey(projectId, episodeId));
  } catch {
    // 清理失败不影响正式保存。
  }
};

const readEpisodeDraft = (projectId, episodeNumber) => {
  if (!episodeNumber) return null;
  try {
    const raw = window.localStorage.getItem(episodeDraftMemoryKey(projectId, episodeNumber));
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const writeEpisodeDraft = (projectId, episodeNumber, draft) => {
  if (!episodeNumber) return;
  try {
    window.localStorage.setItem(episodeDraftMemoryKey(projectId, episodeNumber), JSON.stringify({
      ...draft,
      episodeNumber,
      updatedAt: new Date().toISOString(),
    }));
  } catch {
    // 本地草稿失败不影响正式保存。
  }
};

const removeEpisodeDraft = (projectId, episodeNumber) => {
  if (!episodeNumber) return;
  try {
    window.localStorage.removeItem(episodeDraftMemoryKey(projectId, episodeNumber));
  } catch {
    // 清理失败不影响正式保存。
  }
};

const compactDialogueForPrompt = (messages) => (messages || []).slice(-12).map((message) => ({
  role: message.role,
  title: message.title,
  content: message.content,
  createdAt: message.createdAt,
}));

const outlineEpisodeNumber = (episode, index) => Number(episode?.episode_number || episode?.episodeNumber || index + 1);

const sortEpisodesByOrder = (items) => [...(items || [])].sort((a, b) => {
  const left = Number(a?.sortOrder || 0);
  const right = Number(b?.sortOrder || 0);
  if (left !== right) return left - right;
  return Number(a?.id || 0) - Number(b?.id || 0);
});

const maxEpisodeOrder = (...episodeGroups) => episodeGroups
  .flat()
  .filter(Boolean)
  .reduce((max, episode) => Math.max(max, Number(episode?.sortOrder || episode?.episode_number || episode?.episodeNumber || 0)), 0);

const stripEpisodeTitlePrefix = (title, episodeNumber) => {
  const text = firstText(title);
  if (!text) return "";
  return text
    .replace(new RegExp(`^第\\s*${episodeNumber}\\s*集\\s*[：:·\\-—_\\s]*`), "")
    .replace(/^第\s*\d+\s*集\s*[：:·\-—_\s]*/, "")
    .trim();
};

const formatEpisodeTitle = (episodeNumber, title) => {
  const cleanTitle = stripEpisodeTitlePrefix(title, episodeNumber);
  return cleanTitle ? `第${episodeNumber}集 ${cleanTitle}` : `第${episodeNumber}集`;
};

const episodeDisplayTitle = (episode, episodeNumber, fallback = "未命名") => (
  stripEpisodeTitlePrefix(firstText(episode?.title, episode?.name), episodeNumber) || fallback
);

const WorkButton = ({ icon: Icon, children, active = false, disabled = false, onClick, title }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="h-9 px-3 rounded-md border text-sm font-semibold inline-flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      style={{
        borderColor: active ? theme.colors.accentPrimary : "var(--color-border-primary)",
        backgroundColor: active ? "var(--color-accent-light)" : SURFACE.card,
        color: active ? theme.colors.accentPrimary : "var(--color-text-primary)",
      }}
    >
      {Icon && <Icon size={15} />}
      {children}
    </button>
  );
};

const PanelTitle = ({ icon, title, subtitle }) => {
  const iconNode = React.createElement(icon, { size: 15 });
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <span className="w-7 h-7 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: "var(--color-accent-light)", color: "var(--color-accent-primary)" }}>
          {iconNode}
        </span>
        <div className="min-w-0">
          <div className="text-sm font-bold truncate" style={{ color: "var(--color-text-primary)" }}>{title}</div>
          {subtitle && <div className="text-xs truncate" style={{ color: "var(--color-text-muted)" }}>{subtitle}</div>}
        </div>
      </div>
    </div>
  );
};

const ProductionChatMessage = ({ role = "assistant", title, subtitle, children }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const isUser = role === "user";
  return (
    <div className={`flex gap-3 ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && (
        <span className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: "var(--color-accent-light)", color: theme.colors.accentPrimary }}>
          <Bot size={16} />
        </span>
      )}
      <div
        className="max-w-[820px] min-w-0 rounded-md border px-4 py-3 space-y-2"
        style={{
          borderColor: isUser ? `${theme.colors.accentPrimary}66` : "var(--color-border-primary)",
          backgroundColor: isUser ? "var(--color-accent-light)" : SURFACE.card,
        }}
      >
        {(title || subtitle) && (
          <div className="flex items-center justify-between gap-3">
            {title && <div className="text-sm font-bold truncate" style={{ color: "var(--color-text-primary)" }}>{title}</div>}
            {subtitle && <div className="text-xs shrink-0" style={{ color: "var(--color-text-muted)" }}>{subtitle}</div>}
          </div>
        )}
        <div className="text-sm leading-6 whitespace-pre-wrap" style={{ color: "var(--color-text-secondary)" }}>{children}</div>
      </div>
      {isUser && (
        <span className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: "var(--color-accent-light)", color: theme.colors.accentPrimary }}>
          <MessageSquareText size={16} />
        </span>
      )}
    </div>
  );
};

const EpisodeDialoguePanel = ({
  selectedEpisode,
  messages,
  request,
  onRequestChange,
  onSend,
  loading,
  disabled,
  scriptDirty,
}) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const endRef = useRef(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [messages.length, loading, selectedEpisode?.id]);

  return (
    <section className="rounded-md border overflow-hidden" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
      <div className="border-b px-4 py-3 flex items-center justify-between gap-3" style={{ borderColor: "var(--color-border-primary)" }}>
        <PanelTitle icon={Bot} title="本集导演对话" subtitle={selectedEpisode ? "围绕当前集反复优化" : "发送后会自动创建第一集"} />
        <div className="flex items-center gap-2 shrink-0">
          {scriptDirty && (
            <span className="text-xs px-2 py-1 rounded-md" style={{ backgroundColor: "rgba(212,167,68,0.14)", color: "var(--color-warning)" }}>
              编辑器有未保存修改
            </span>
          )}
          <span className="text-xs px-2 py-1 rounded-md" style={{ backgroundColor: "var(--color-accent-light)", color: theme.colors.accentPrimary }}>
            当前集独立记忆
          </span>
        </div>
      </div>

      <div className="max-h-[360px] overflow-y-auto custom-scrollbar p-4 space-y-4" style={{ backgroundColor: SURFACE.soft }}>
        <ProductionChatMessage title="导演助手" subtitle="等待你的要求">
          {selectedEpisode
            ? "你可以让我写本集、重写开场、增强反转、压缩对白，或者根据你在下方编辑器里手动改过的版本继续优化。发送时我会参考已确认立项资料、当前集大纲、前后集摘要和编辑器里的最新文本。"
            : "可以直接输入这一集的要求并发送。我会先自动创建第一集，再参考已确认立项资料、当前集大纲和前情来写。"}
        </ProductionChatMessage>
        {messages.map((message) => (
          <ProductionChatMessage
            key={message.id}
            role={message.role}
            title={message.title}
            subtitle={message.subtitle}
          >
            {message.content}
          </ProductionChatMessage>
        ))}
        <div ref={endRef} aria-hidden="true" />
      </div>

      <div className="relative border-t p-3 space-y-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card }} aria-busy={loading}>
        {loading && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center px-4 backdrop-blur-sm animate-in fade-in duration-150"
            style={{
              backgroundColor: "color-mix(in srgb, var(--color-bg-card) 86%, transparent)",
              color: "var(--color-text-primary)",
            }}
          >
            <div className="rounded-md border px-4 py-3 flex items-center gap-3 shadow-lg" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
              <Loader2 size={18} className="animate-spin" style={{ color: theme.colors.accentPrimary }} />
              <div>
                <div className="text-sm font-semibold">导演正在思考中</div>
                <div className="text-sm mt-0.5" style={{ color: "var(--color-text-muted)" }}>正在根据当前集上下文整理剧本。</div>
              </div>
            </div>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Sparkles size={14} style={{ color: theme.colors.accentPrimary }} />
          <span className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>给本集导演的要求</span>
          <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>留空也可以直接生成/优化</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_140px] gap-3">
          <textarea
            value={request}
            onChange={(event) => onRequestChange(event.target.value)}
            rows={3}
            disabled={disabled || loading}
            className="w-full resize-none rounded-md border px-3 py-2 text-sm leading-6 outline-none"
            style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.input, color: "var(--color-text-primary)" }}
            placeholder={selectedEpisode ? "例如：这一集开头要更抓人；女主不要哭，改成冷静反击；结尾加一个反转悬念。" : "例如：先写第一集，开头要强钩子，30秒内出现误会和反转。"}
          />
          <button
            type="button"
            onClick={onSend}
            disabled={disabled || loading}
            className="rounded-md border text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            style={{ borderColor: theme.colors.accentPrimary, backgroundColor: theme.colors.accentPrimary, color: "white" }}
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            {selectedEpisode ? "发送给导演" : "新建并发送"}
          </button>
        </div>
      </div>
    </section>
  );
};

const EpisodeProductionWorkbench = ({ projectId }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { getTheme } = useTheme();
  const theme = getTheme();
  const [mockAgentEnabled, setMockAgentEnabledState] = useState(() => isMockAgentEnabled());
  const [episodes, setEpisodes] = useState([]);
  const [selectedEpisodeId, setSelectedEpisodeId] = useState("");
  const [draftEpisode, setDraftEpisode] = useState(null);
  const [scriptDraft, setScriptDraft] = useState("");
  const [productionState, setProductionState] = useState(null);
  const [directorRequest, setDirectorRequest] = useState("");
  const [episodeMessages, setEpisodeMessages] = useState([]);
  const [lastSavedScript, setLastSavedScript] = useState("");
  const [loading, setLoading] = useState("");
  const [error, setError] = useState("");
  const [productionMemory, setProductionMemory] = useState(() => normalizeProductionMemory(null));
  const [memoryReady, setMemoryReady] = useState(false);
  const memorySaveTimerRef = useRef(null);
  const productionMemoryRef = useRef(productionMemory);
  const [fullAutoProduction, setFullAutoProduction] = useState(false);
  const fullAutoProductionKey = useMemo(
    () => projectId ? `dialogue_production_full_auto:${projectId}` : "dialogue_production_full_auto:global",
    [projectId]
  );

  useEffect(() => {
    productionMemoryRef.current = productionMemory;
  }, [productionMemory]);

  useEffect(() => {
    try {
      setFullAutoProduction(window.localStorage.getItem(fullAutoProductionKey) === "true");
    } catch {
      setFullAutoProduction(false);
    }
  }, [fullAutoProductionKey]);

  const toggleFullAutoProduction = useCallback(() => {
    const nextEnabled = !fullAutoProduction;
    setFullAutoProduction(nextEnabled);
    try {
      window.localStorage.setItem(fullAutoProductionKey, nextEnabled ? "true" : "false");
    } catch {
      // Ignore storage failures.
    }
  }, [fullAutoProduction, fullAutoProductionKey]);

  useEffect(() => {
    const syncMockAgent = () => setMockAgentEnabledState(isMockAgentEnabled());
    window.addEventListener("storage", syncMockAgent);
    window.addEventListener("ai-provider-change", syncMockAgent);
    return () => {
      window.removeEventListener("storage", syncMockAgent);
      window.removeEventListener("ai-provider-change", syncMockAgent);
    };
  }, []);

  const selectedEpisode = useMemo(
    () => episodes.find((episode) => String(episode.id) === String(selectedEpisodeId)) || null,
    [episodes, selectedEpisodeId]
  );
  const workingEpisode = selectedEpisode || draftEpisode;
  const dialogueMemoryId = selectedEpisode?.id || draftEpisode?.draftId || "";
  const workingEpisodeNumber = Number(workingEpisode?.sortOrder || 0);
  const workingEpisodeKey = selectedEpisode
    ? `episode:${selectedEpisode.id}`
    : draftEpisode
      ? `draft:${draftEpisode.draftId}`
      : "";

  const readDialogueMemory = useCallback((memoryId) => {
    if (!memoryId) return [];
    const serverValue = productionMemoryRef.current?.dialogues?.[String(memoryId)];
    if (Array.isArray(serverValue)) return serverValue;
    return readEpisodeDialogue(projectId, memoryId);
  }, [projectId]);

  const writeDialogueMemory = useCallback((memoryId, messages) => {
    if (!memoryId) return;
    const nextMessages = (messages || []).slice(-30);
    writeEpisodeDialogue(projectId, memoryId, nextMessages);
    setProductionMemory((current) => ({
      ...normalizeProductionMemory(current),
      dialogues: {
        ...normalizeProductionMemory(current).dialogues,
        [String(memoryId)]: nextMessages,
      },
    }));
  }, [projectId]);

  const removeDialogueMemory = useCallback((memoryId) => {
    if (!memoryId) return;
    removeEpisodeDialogue(projectId, memoryId);
    setProductionMemory((current) => {
      const normalized = normalizeProductionMemory(current);
      const nextDialogues = { ...normalized.dialogues };
      delete nextDialogues[String(memoryId)];
      return { ...normalized, dialogues: nextDialogues };
    });
  }, [projectId]);

  const readDraftMemory = useCallback((episodeNumber) => {
    if (!episodeNumber) return null;
    const serverValue = productionMemoryRef.current?.drafts?.[String(episodeNumber)];
    if (serverValue && typeof serverValue === "object") return serverValue;
    return readEpisodeDraft(projectId, episodeNumber);
  }, [projectId]);

  const writeDraftMemory = useCallback((episodeNumber, draft) => {
    if (!episodeNumber) return;
    const nextDraft = {
      ...draft,
      episodeNumber,
      updatedAt: new Date().toISOString(),
    };
    writeEpisodeDraft(projectId, episodeNumber, nextDraft);
    setProductionMemory((current) => ({
      ...normalizeProductionMemory(current),
      drafts: {
        ...normalizeProductionMemory(current).drafts,
        [String(episodeNumber)]: nextDraft,
      },
    }));
  }, [projectId]);

  const removeDraftMemory = useCallback((episodeNumber) => {
    if (!episodeNumber) return;
    removeEpisodeDraft(projectId, episodeNumber);
    setProductionMemory((current) => {
      const normalized = normalizeProductionMemory(current);
      const nextDrafts = { ...normalized.drafts };
      delete nextDrafts[String(episodeNumber)];
      return { ...normalized, drafts: nextDrafts };
    });
  }, [projectId]);

  const loadProjectData = useCallback(async () => {
    if (!projectId) return;
    setLoading("load");
    setError("");
    try {
      setMemoryReady(false);
      const [stateData, episodeData, memoryData] = await Promise.all([
        authenticatedFetch(`${API_HOST}/api/adaptation-agent/projects/${projectId}/production-state`),
        authenticatedFetch(`${API_HOST}/api/project/episode/list?projectId=${projectId}`),
        authenticatedFetch(`${API_HOST}/api/adaptation-agent/projects/${projectId}/workspace-state/${PRODUCTION_WORKSPACE_STATE_KEY}`).catch(() => null),
      ]);
      setProductionState(stateData || null);
      setProductionMemory(normalizeProductionMemory(memoryData));
      setMemoryReady(true);
      const episodeList = Array.isArray(episodeData) ? episodeData : [];
      setEpisodes(episodeList);
      setSelectedEpisodeId((current) => {
        if (current && episodeList.some((episode) => String(episode.id) === String(current))) return current;
        return episodeList[0]?.id ? String(episodeList[0].id) : "";
      });
    } catch (err) {
      setError(err.message || "加载分集生产数据失败");
      toast.error(err.message || "加载分集生产数据失败", 0);
      setMemoryReady(true);
    } finally {
      setLoading("");
    }
  }, [projectId, toast]);

  useEffect(() => {
    loadProjectData();
  }, [loadProjectData]);

  useEffect(() => {
    if (!projectId || !memoryReady) return undefined;
    if (memorySaveTimerRef.current) {
      clearTimeout(memorySaveTimerRef.current);
    }
    const payload = normalizeProductionMemory(productionMemory);
    memorySaveTimerRef.current = setTimeout(() => {
      authenticatedFetch(`${API_HOST}/api/adaptation-agent/projects/${projectId}/workspace-state/${PRODUCTION_WORKSPACE_STATE_KEY}`, {
        method: "POST",
        body: JSON.stringify(payload),
      }).catch(() => {
        // 后端状态保存失败时仍保留 localStorage 兜底。
      });
    }, 600);
    return () => {
      if (memorySaveTimerRef.current) {
        clearTimeout(memorySaveTimerRef.current);
      }
    };
  }, [memoryReady, productionMemory, projectId]);

  useEffect(() => {
    if (!workingEpisodeKey || !workingEpisode) {
      setScriptDraft("");
      setLastSavedScript("");
      return;
    }
    const episodeNumber = Number(workingEpisode.sortOrder || 0);
    const savedScript = selectedEpisode?.scriptContent || "";
    const storedDraft = readDraftMemory(episodeNumber);
    const nextScript = storedDraft ? (storedDraft.scriptContent || "") : (workingEpisode.scriptContent || "");
    setScriptDraft((current) => current === nextScript ? current : nextScript);
    setLastSavedScript((current) => current === savedScript ? current : savedScript);
  }, [readDraftMemory, selectedEpisode?.scriptContent, workingEpisode, workingEpisodeKey]);

  useEffect(() => {
    setDirectorRequest("");
    const nextMessages = readDialogueMemory(dialogueMemoryId);
    setEpisodeMessages((current) => sameJson(current, nextMessages) ? current : nextMessages);
  }, [dialogueMemoryId, readDialogueMemory]);

  useEffect(() => {
    if (!dialogueMemoryId) return;
    writeDialogueMemory(dialogueMemoryId, episodeMessages);
  }, [dialogueMemoryId, episodeMessages, writeDialogueMemory]);

  useEffect(() => {
    if (!projectId || !workingEpisode || !workingEpisodeNumber) return;
    const savedScript = selectedEpisode?.scriptContent || "";
    if (selectedEpisode && scriptDraft === savedScript) {
      removeDraftMemory(workingEpisodeNumber);
      return;
    }
    writeDraftMemory(workingEpisodeNumber, {
      draftId: draftEpisode?.draftId || `draft-${workingEpisodeNumber}`,
      title: workingEpisode.title || `第${workingEpisodeNumber}集`,
      scriptContent: scriptDraft,
      sourceEpisodeId: selectedEpisode?.id || null,
    });
  }, [draftEpisode?.draftId, projectId, removeDraftMemory, scriptDraft, selectedEpisode, workingEpisode, workingEpisodeNumber, writeDraftMemory]);

  const scriptDirty = Boolean(workingEpisode && scriptDraft !== lastSavedScript);

  const outlineEpisodes = Array.isArray(productionState?.outlineEpisodes) ? productionState.outlineEpisodes : [];

  const nextEpisodeNumber = ({ appendToOutline = false } = {}) => {
    const base = appendToOutline
      ? maxEpisodeOrder(episodes, draftEpisode, outlineEpisodes)
      : maxEpisodeOrder(episodes, draftEpisode);
    return base + 1;
  };

  const createDraftEpisode = ({ announce = true, appendToOutline = false } = {}) => {
    const nextNumber = nextEpisodeNumber({ appendToOutline });
    const outlineTitle = productionState?.outlineEpisodes?.find((item, index) => outlineEpisodeNumber(item, index) === nextNumber)?.title;
    const storedDraft = readDraftMemory(nextNumber);
    const draft = {
      draft: true,
      draftId: `draft-${nextNumber}`,
      sortOrder: nextNumber,
      title: firstText(storedDraft?.title, formatEpisodeTitle(nextNumber, outlineTitle || "新增单集")),
      scriptContent: storedDraft?.scriptContent || "",
    };
    setDraftEpisode(draft);
    setSelectedEpisodeId("");
    setScriptDraft(draft.scriptContent);
    setLastSavedScript("");
    setEpisodeMessages(readDialogueMemory(draft.draftId));
    if (announce) toast.success(`已新建第 ${nextNumber} 集，保存前不会进入项目`);
    return draft;
  };

  const createEpisode = async () => {
    createDraftEpisode({ appendToOutline: true });
  };

  const persistScriptToEpisode = async ({
    targetEpisode,
    targetScript,
    targetTitle,
    silent = false,
  }) => {
    if (!targetEpisode) {
      toast.warning("请先新建一集或生成本集内容");
      return null;
    }
    if (!targetScript?.trim()) {
      toast.warning("当前没有可保存的剧本文本");
      return null;
    }
    const episodeNumber = targetEpisode?.sortOrder || episodes.length + 1;
    const outlineTitle = productionState?.outlineEpisodes?.find((item, index) => outlineEpisodeNumber(item, index) === episodeNumber)?.title;
    const savedResult = await authenticatedFetch(`${API_HOST}/api/script-center/import-episode`, {
      method: "POST",
      body: JSON.stringify({
        projectId: Number(projectId),
        episodeNumber,
        title: formatEpisodeTitle(episodeNumber, firstText(outlineTitle, targetTitle, targetEpisode?.title)),
        content: targetScript,
        sourceType: "ADAPTATION_AGENT",
        sourceId: productionState?.sessionId ? String(productionState.sessionId) : "",
        mode: "upsert",
        allowExistingUpdate: !silent,
      }),
    });
    const saved = savedResult?.episode || savedResult;
    const previousDraftId = targetEpisode?.draftId;
    setEpisodes((current) => {
      const exists = current.some((episode) => String(episode.id) === String(saved.id));
      if (!exists) return sortEpisodesByOrder([...current, saved]);
      return sortEpisodesByOrder(current.map((episode) => (
        String(episode.id) === String(saved.id) ? saved : episode
      )));
    });
    setLastSavedScript(saved?.scriptContent || targetScript);
    setDraftEpisode(null);
    setSelectedEpisodeId(String(saved.id));
    removeDraftMemory(episodeNumber);
    if (previousDraftId && saved?.id) {
      removeDialogueMemory(previousDraftId);
    }
    const saveMessage = {
      id: `s-${Date.now()}`,
      role: "assistant",
      title: savedResult?.created ? "已保存为本集剧本" : "已更新本集剧本",
      subtitle: silent ? "全自动已处理" : "剧本中心已接收",
      content: silent
        ? "全自动已开启，导演回稿已直接保存为本集剧本。"
        : "当前编辑器里的本集剧本已保存。后续拆资产、拆分镜和生成视频提示词都会以这个版本为准。",
      createdAt: new Date().toISOString(),
    };
    setEpisodeMessages((current) => {
      const next = [...current, saveMessage].slice(-30);
      writeDialogueMemory(saved.id, next);
      return next;
    });
    if (!silent) {
      toast.success(savedResult?.created ? "已保存为本集剧本" : "已更新本集剧本");
    }
    return saved;
  };

  const generateEpisodeScript = async () => {
    if (!productionState?.ready) {
      toast.warning("请先在智能改编中确认分集大纲，完成立项后再生成分集剧本。");
      return;
    }
    setLoading("generateScript");
    let targetEpisode = selectedEpisode;
    let targetIndex = selectedIndex;
    let targetScript = scriptDraft;
    let targetMessages = episodeMessages;
    if (!targetEpisode) {
      targetEpisode = draftEpisode || createDraftEpisode({ announce: false });
      targetIndex = Math.max(0, (targetEpisode.sortOrder || episodes.length + 1) - 1);
      targetScript = scriptDraft || targetEpisode.scriptContent || "";
      targetMessages = readDialogueMemory(targetEpisode.draftId);
    }
    const requestText = firstText(
      directorRequest,
      targetScript ? "请参考编辑器里的最新版本，继续优化这一集剧本。" : "请按当前集大纲写出这一集剧本。"
    );
    const userMessage = {
      id: `u-${Date.now()}`,
      role: "user",
      title: "你的要求",
      subtitle: episodeLabel(targetEpisode, targetIndex),
      content: requestText,
      createdAt: new Date().toISOString(),
    };
    const nextHistory = [...targetMessages, userMessage].slice(-30);
    writeDialogueMemory(targetEpisode.id || targetEpisode.draftId, nextHistory);
    setEpisodeMessages(nextHistory);
    try {
      const provider = localStorage.getItem("ai_provider") || "omini";
      const taskType = targetEpisode.id
        ? "ADAPTATION_PRODUCTION_EPISODE_SCRIPT"
        : "ADAPTATION_PRODUCTION_EPISODE_SCRIPT_DRAFT";
      const taskPayload = {
          projectId: Number(projectId),
          ...(targetEpisode.id ? { episodeId: Number(targetEpisode.id) } : {}),
          episodeNumber: targetEpisode.sortOrder,
          title: targetEpisode.title,
          userRequest: requestText,
          existingScript: targetScript,
          conversationHistory: compactDialogueForPrompt(nextHistory),
      };
      const taskOptions = {
        projectId: Number(projectId),
        provider,
        source: targetEpisode.id ? "adaptation_production_episode_script" : "adaptation_production_episode_script_draft",
        timeoutMs: 30 * 60 * 1000,
      };
      if (targetEpisode.id) {
        taskOptions.episodeId = Number(targetEpisode.id);
      }
      const task = await runTaskToCompletion(taskType, taskPayload, taskOptions);
      const data = task?.result || {};
      const generatedScript = data?.script || "";
      const generatedTitle = data?.title || targetEpisode.title;
      writeDraftMemory(targetEpisode.sortOrder, {
        draftId: targetEpisode.draftId || `draft-${targetEpisode.sortOrder}`,
        title: generatedTitle,
        scriptContent: generatedScript,
        sourceEpisodeId: targetEpisode.id || null,
      });
      setScriptDraft(generatedScript);
      if (!targetEpisode.id) {
        setDraftEpisode((current) => current ? {
          ...current,
          title: generatedTitle || current.title,
          scriptContent: generatedScript,
        } : current);
      }
      const notes = Array.isArray(data?.directorNotes)
        ? data.directorNotes.filter(Boolean).join("\n")
        : firstText(data?.directorNotes);
      const directorReply = formatDirectorNotes(data, notes);
      setEpisodeMessages((current) => [...current, {
        id: `a-${Date.now()}`,
        role: "assistant",
        title: "导演回稿",
        subtitle: "已回填到编辑器",
        content: directorReply || `已生成 ${data?.script?.length || 0} 字本集内容，已经放到下方编辑器。满意后点击“保存为本集剧本”；也可以继续提要求让我改。`,
        createdAt: new Date().toISOString(),
      }].slice(-30));
      if (data?.title) {
        setEpisodes((current) => current.map((episode) => (
          String(episode.id) === String(targetEpisode.id)
            ? { ...episode, title: data.title }
            : episode
        )));
      }
      setDirectorRequest("");
      if (fullAutoProduction && generatedScript.trim() && !targetEpisode.id) {
        await persistScriptToEpisode({
          targetEpisode: {
            ...targetEpisode,
            title: generatedTitle || targetEpisode.title,
            scriptContent: generatedScript,
          },
          targetScript: generatedScript,
          targetTitle: generatedTitle,
          silent: true,
        });
        toast.success("全自动已开启，本集剧本已自动保存");
      } else if (fullAutoProduction && generatedScript.trim() && targetEpisode.id) {
        toast.warning("本集已有剧本，自动回稿已放入编辑器但不会覆盖；确认后请手动保存");
      } else {
        toast.success("导演已写好本集内容，确认后可保存为本集剧本");
      }
    } catch (err) {
      setEpisodeMessages((current) => [...current, {
        id: `e-${Date.now()}`,
        role: "assistant",
        title: "生成失败",
        subtitle: "可调整后重试",
        content: err.message || "生成本集剧本失败",
        createdAt: new Date().toISOString(),
      }].slice(-30));
      toast.error(err.message || "生成本集剧本失败", 0);
    } finally {
      setLoading("");
    }
  };

  const saveScript = async () => {
    if (!workingEpisode) {
      toast.warning("请先新建一集或生成本集内容");
      return;
    }
    if (!scriptDraft.trim()) {
      toast.warning("当前没有可保存的剧本文本");
      return;
    }
    setLoading("saveScript");
    try {
      await persistScriptToEpisode({
        targetEpisode: workingEpisode,
        targetScript: scriptDraft,
        targetTitle: workingEpisode?.title,
      });
    } catch (err) {
      toast.error(err.message || "保存本集剧本失败", 0);
    } finally {
      setLoading("");
    }
  };

  const goEpisodeTab = (tab) => {
    if (!selectedEpisode) {
      toast.warning("请先保存为本集剧本");
      return;
    }
    navigate(`/project/${projectId}/episode/${selectedEpisode.id}/${tab}`);
  };

  const toggleMockAgent = () => {
    const nextEnabled = !mockAgentEnabled;
    setMockAgentEnabled(nextEnabled);
    setMockAgentEnabledState(nextEnabled);
    toast.success(nextEnabled ? "已开启演示模式，本页会使用示例内容" : "已关闭演示模式，恢复正式生成");
  };

  const selectedIndex = episodes.findIndex((episode) => String(episode.id) === String(selectedEpisodeId));
  const workingIndex = selectedEpisode ? selectedIndex : Math.max(0, (draftEpisode?.sortOrder || episodes.length + 1) - 1);
  const saveScriptLabel = selectedEpisode ? "更新本集剧本" : "保存为本集剧本";
  const seriesTitle = firstText(productionState?.seriesBible?.title, productionState?.sessionTitle, "未立项");
  const seriesLogline = firstText(productionState?.seriesBible?.logline, productionState?.sourceMaterial?.summary);
  const visualStyle = firstText(
    productionState?.styleAnchor?.visual_style_anchor?.overall_style,
    productionState?.styleAnchor?.style_prompt,
  );
  const outlineEntryMap = new Map();
  outlineEpisodes.forEach((outline, index) => {
    const number = outlineEpisodeNumber(outline, index);
    if (number > 0) {
      outlineEntryMap.set(number, { ...outline, episode_number: number });
    }
  });
  [...episodes, draftEpisode].filter(Boolean).forEach((episode) => {
    const number = Number(episode?.sortOrder || 0);
    if (number <= 0 || outlineEntryMap.has(number)) return;
    outlineEntryMap.set(number, {
      episode_number: number,
      title: episodeDisplayTitle(episode, number, "新增单集"),
      summary: episode?.scriptContent ? shortText(episode.scriptContent, 120) : "新增集，可在左侧编辑剧本。",
      added: true,
    });
  });
  const visibleOutlineEpisodes = [...outlineEntryMap.values()].sort((a, b) => outlineEpisodeNumber(a, 0) - outlineEpisodeNumber(b, 0));

  const selectOutlineEpisode = (outline, index) => {
    const episodeNumber = outlineEpisodeNumber(outline, index);
    const existing = episodes.find((episode, episodeIndex) => outlineEpisodeNumber(episode, episodeIndex) === episodeNumber || episode.sortOrder === episodeNumber);
    if (existing) {
      setSelectedEpisodeId(String(existing.id));
      return;
    }
    if (draftEpisode?.sortOrder === episodeNumber) {
      setSelectedEpisodeId("");
      return;
    }
    const storedDraft = readDraftMemory(episodeNumber);
    const draft = {
      draft: true,
      draftId: `draft-${episodeNumber}`,
      sortOrder: episodeNumber,
      title: firstText(storedDraft?.title, formatEpisodeTitle(episodeNumber, outline?.title)),
      scriptContent: storedDraft?.scriptContent || "",
    };
    setDraftEpisode(draft);
    setSelectedEpisodeId("");
    setScriptDraft(draft.scriptContent);
    setLastSavedScript("");
    setEpisodeMessages(readDialogueMemory(draft.draftId));
  };

  return (
    <div className="w-full h-full overflow-hidden flex flex-col" style={{ backgroundColor: SURFACE.page }}>
      <header className="h-16 shrink-0 border-b px-4 flex items-center justify-between gap-4" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-9 h-9 rounded-md flex items-center justify-center" style={{ backgroundColor: "var(--color-accent-light)", color: theme.colors.accentPrimary }}>
            <Bot size={18} />
          </span>
          <div className="min-w-0">
            <div className="text-base font-bold truncate" style={{ color: "var(--color-text-primary)" }}>分集生产台</div>
            <div className="text-sm truncate" style={{ color: "var(--color-text-muted)" }}>
              立项后一次只处理当前集，写完可保存、可拆分镜、可继续补新集
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <WorkButton
            icon={fullAutoProduction ? ToggleRight : ToggleLeft}
            active={fullAutoProduction}
            onClick={toggleFullAutoProduction}
            title="开启后导演回稿会直接保存为本集剧本，默认关闭"
          >
            全自动{fullAutoProduction ? "开" : "关"}
          </WorkButton>
          <WorkButton icon={Bot} active={mockAgentEnabled} onClick={toggleMockAgent} title="使用示例数据快速体验">
            {mockAgentEnabled ? "演示中" : "演示模式"}
          </WorkButton>
          <WorkButton icon={RefreshCw} onClick={loadProjectData} disabled={loading === "load"}>{loading === "load" ? "刷新中" : "刷新"}</WorkButton>
          <WorkButton icon={FileText} onClick={() => navigate(`/project/${projectId}/script-analysis`)}>剧本专区</WorkButton>
          <WorkButton icon={Plus} active onClick={createEpisode} disabled={loading === "create"}>
            新建一集
          </WorkButton>
        </div>
      </header>

      {error && (
        <div className="mx-4 mt-4 rounded-md border px-3 py-2 text-sm flex items-center gap-2 animate-in fade-in slide-in-from-top-2 duration-200" style={{ borderColor: "var(--color-error)", backgroundColor: "rgba(220,38,38,0.1)", color: "var(--color-error)" }}>
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      {productionState && !productionState.ready ? (
        <div className="flex-1 min-h-0 flex items-center justify-center p-6">
          <div className="max-w-[720px] w-full rounded-md border p-6 space-y-5" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
            <PanelTitle icon={Bot} title="分集生产台尚未开放" subtitle="需要先完成项目级立项" />
            <div className="text-base leading-7" style={{ color: "var(--color-text-secondary)" }}>
              {productionState.message || "请先在智能改编或小说转剧本中确认分集大纲。确认后，这里会按集写剧本、识别资产、生成分镜和视频提示词。"}
            </div>
            <div className="rounded-md border p-4 text-sm leading-6" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card, color: "var(--color-text-muted)" }}>
              立项台只保留：素材确认、内容理解、改编方案、短剧设定、风格定调、分集大纲。后续分集剧本和制作内容会在这里按集推进。
            </div>
            <div className="flex items-center gap-2">
              <WorkButton icon={Bot} active onClick={() => navigate(`/project/${projectId}/adaptation-agent`)}>回到立项台</WorkButton>
              <WorkButton icon={BookOpen} onClick={() => navigate(`/project/${projectId}/novel-agent`)}>小说转剧本</WorkButton>
              <WorkButton icon={RefreshCw} onClick={loadProjectData} disabled={loading === "load"}>{loading === "load" ? "刷新中" : "刷新状态"}</WorkButton>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 grid grid-cols-1 xl:grid-cols-[minmax(0,3fr)_minmax(360px,1fr)]">
          <main className="min-w-0 min-h-0 flex flex-col border-r" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.page }}>
            <div className="shrink-0 border-b px-4 py-3 flex flex-wrap items-center justify-between gap-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
              <PanelTitle
                icon={FileText}
                title={workingEpisode ? `${episodeLabel(workingEpisode, workingIndex)}剧本编辑` : "单集剧本编辑器"}
                subtitle={workingEpisode?.title || "先在右侧发送给导演生成内容，也可以新建一集后手写"}
              />
              <div className="flex flex-wrap items-center gap-2">
                <WorkButton icon={Plus} onClick={createEpisode}>新建一集</WorkButton>
                <WorkButton icon={Save} active onClick={saveScript} disabled={!workingEpisode || !scriptDraft.trim() || loading === "saveScript"}>
                  {loading === "saveScript" ? "保存中" : saveScriptLabel}
                </WorkButton>
                <WorkButton icon={Film} onClick={() => goEpisodeTab("storyboard-board")} disabled={!selectedEpisode}>故事板导演台</WorkButton>
                <WorkButton icon={Clapperboard} onClick={() => goEpisodeTab("storyboard")} disabled={!selectedEpisode}>打开分镜</WorkButton>
              </div>
            </div>

            <div className="shrink-0 px-4 py-2 border-b flex items-center justify-between gap-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card }}>
              <div className="text-sm" style={{ color: "var(--color-text-muted)" }}>
                {selectedEpisode
                  ? "当前剧本已保存为正式单集，可继续编辑后更新本集剧本。"
                  : scriptDraft.trim()
                    ? "当前内容还是草稿，点击“保存为本集剧本”后才会进入项目剧本。"
                    : "编辑器占主要区域，右侧用于和导演沟通、查看大纲。"}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {scriptDirty && (
                  <span className="text-xs px-2 py-1 rounded-md" style={{ backgroundColor: "rgba(212,167,68,0.14)", color: "var(--color-warning)" }}>
                    未保存
                  </span>
                )}
                <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>{scriptDraft.length.toLocaleString()} 字</span>
              </div>
            </div>

            <textarea
              value={scriptDraft}
              onChange={(event) => {
                const nextScript = event.target.value;
                const activeEpisode = workingEpisode || createDraftEpisode({ announce: false });
                setScriptDraft(nextScript);
                if (activeEpisode?.draft) {
                  setDraftEpisode((current) => current ? { ...current, scriptContent: nextScript } : current);
                }
              }}
              placeholder={workingEpisode ? "在这里写或粘贴当前集剧本。右侧导演回稿会回填到这里。" : "可以直接在这里写第一集内容，也可以在右侧让导演生成。"}
              className="flex-1 min-h-0 w-full resize-none bg-transparent p-6 text-base leading-8 outline-none"
              style={{ color: "var(--color-text-primary)" }}
            />
          </main>

          <aside className="min-h-0 overflow-y-auto custom-scrollbar p-3 space-y-3" style={{ backgroundColor: SURFACE.panel }}>
            <EpisodeDialoguePanel
              selectedEpisode={workingEpisode}
              messages={episodeMessages}
              request={directorRequest}
              onRequestChange={setDirectorRequest}
              onSend={generateEpisodeScript}
              loading={loading === "generateScript"}
              disabled={!productionState?.ready}
              scriptDirty={scriptDirty}
            />

            <section className="rounded-md border p-3 space-y-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card }}>
              <PanelTitle icon={Database} title="分集大纲" subtitle={`${visibleOutlineEpisodes.length || 0} 集`} />
              <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft }}>
                <div className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>{seriesTitle}</div>
                {seriesLogline && <div className="text-sm leading-6" style={{ color: "var(--color-text-muted)" }}>{shortText(seriesLogline, 120)}</div>}
                {visualStyle && <div className="text-xs leading-5" style={{ color: "var(--color-text-muted)" }}>风格：{shortText(visualStyle, 80)}</div>}
              </div>
              <div className="space-y-2 max-h-[420px] overflow-y-auto custom-scrollbar pr-1">
                {visibleOutlineEpisodes.length ? visibleOutlineEpisodes.map((outline, index) => {
                  const number = outlineEpisodeNumber(outline, index);
                  const existing = episodes.find((episode, episodeIndex) => outlineEpisodeNumber(episode, episodeIndex) === number || episode.sortOrder === number);
                  const active = (selectedEpisode && existing && String(selectedEpisode.id) === String(existing.id))
                    || (!selectedEpisode && draftEpisode?.sortOrder === number);
                  const displayTitle = episodeDisplayTitle(outline, number, outline.added ? "新增单集" : "未命名");
                  return (
                    <button
                      key={`${number}-${outline.title || index}`}
                      type="button"
                      onClick={() => selectOutlineEpisode(outline, index)}
                      className="w-full rounded-md border p-3 text-left cursor-pointer transition-colors"
                      style={{
                        borderColor: active ? theme.colors.accentPrimary : "var(--color-border-primary)",
                        backgroundColor: active ? "var(--color-accent-light)" : SURFACE.panel,
                      }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-bold truncate" style={{ color: "var(--color-text-primary)" }}>第{number}集 · {displayTitle}</div>
                        <span className="text-xs px-1.5 py-0.5 rounded" style={existing ? TONES.ready : outline.added ? TONES.warning : TONES.muted}>
                          {existing ? "已保存" : outline.added ? "新增" : "未保存"}
                        </span>
                      </div>
                      <div className="text-xs leading-5 mt-1 line-clamp-3" style={{ color: "var(--color-text-muted)" }}>
                        {outline.summary || outline.opening_hook || outline.conflict || "点击后开始写这一集。"}
                      </div>
                    </button>
                  );
                }) : (
                  <div className="rounded-md border p-3 text-sm leading-6" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)", backgroundColor: SURFACE.panel }}>
                    暂时没有分集大纲。可以先新建一集，或回到立项台补齐分集大纲。
                  </div>
                )}
              </div>
            </section>
          </aside>
        </div>
      )}
    </div>
  );
};

export default EpisodeProductionWorkbench;
